import cloneDeep from 'lodash/cloneDeep';
import findKey from 'lodash/findKey';

import type { IViewDataInitialState } from 'src/redux/viewData/types';

const SECTION_KEY = 'ACR_SECTIONS_DETAIL';

interface NarrativeRef {
  id: number;
}

type NarrativeValue = NarrativeRef & Record<string, unknown>;

interface NarrativeIdAction {
  narrativeId: number;
}

interface PayloadAction<T = unknown> {
  payload: T;
}

export const receiveNarrative = (
  state: IViewDataInitialState,
  action: PayloadAction,
): IViewDataInitialState => {
  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.content = action.payload;
  return newState;
};

export const receiveMultiNarrative = (
  state: IViewDataInitialState,
  action: PayloadAction<NarrativeValue[]>,
): IViewDataInitialState => {
  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.narratives = action.payload;
  return newState;
};

export const receiveMultiNarrativeSingle = (
  state: IViewDataInitialState,
  action: PayloadAction<{ narrative_id: number; content: string }>,
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.payload.narrative_id,
  );

  if (narrKey === -1) return state;

  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.narratives[narrKey] = {
    ...newState[SECTION_KEY].data.narratives[narrKey],
    content: action.payload.content,
  };

  return newState;
};

export const receiveExtraNarrative = (
  state: IViewDataInitialState,
  action: PayloadAction<NarrativeValue[]>,
): IViewDataInitialState => {
  const newState = cloneDeep(state);

  let extraNarratives = newState[SECTION_KEY].data.extra_narratives;

  extraNarratives = extraNarratives.map((item: NarrativeRef) => {
    const narrItem = action.payload.find(narr => narr.id === item.id);

    return {
      ...item,
      ...narrItem,
    };
  });

  return {
    ...state,
    [SECTION_KEY]: {
      ...state[SECTION_KEY],
      data: {
        ...state[SECTION_KEY].data,
        extra_narratives: extraNarratives,
      },
    },
  };
};

export const lockMultiNarrativeSuccess = (
  state: IViewDataInitialState,
  action: NarrativeIdAction & { editor: string; content?: string },
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.narrativeId,
  );

  if (narrKey === -1) return state;

  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.narratives[narrKey] = {
    ...newState[SECTION_KEY].data.narratives[narrKey],
    i_am_editor: true,
    editor: action.editor,
    locked_at: new Date().toISOString(),
    locked: true,
    content: action.content,
  };

  return newState;
};

export const cancelMultiNarrativeSuccess = (
  state: IViewDataInitialState,
  action: { value: NarrativeValue },
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.value.id,
  );

  if (narrKey === -1) return state;

  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.narratives[narrKey] = {
    ...action.value,
    canceled: true,
  };
  return newState;
};

export const saveMultiNarrative = (
  state: IViewDataInitialState,
  action: NarrativeIdAction,
): IViewDataInitialState => ({
  ...state,
  [SECTION_KEY]: {
    ...state[SECTION_KEY],
    isFetchingMultinarrative: action.narrativeId,
  },
});

export const saveMultiNarrativeFailed = (
  state: IViewDataInitialState,
): IViewDataInitialState => ({
  ...state,
  [SECTION_KEY]: {
    ...state[SECTION_KEY],
    isFetchingMultinarrative: undefined,
  },
});

export const saveMultiNarrativeSuccess = (
  state: IViewDataInitialState,
  action: { value: NarrativeValue; autosave: boolean },
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.value.id,
  );

  if (narrKey === -1) return state;

  const notification = action.autosave ? { autosaved: true } : { saved: true };

  const newState = cloneDeep(state);
  newState[SECTION_KEY].isFetchingMultinarrative = undefined;
  newState[SECTION_KEY].data.narratives[narrKey] = {
    ...action.value,
    ...notification,
  };
  return newState;
};

export const resetMultiNarrative = (
  state: IViewDataInitialState,
  action: NarrativeIdAction,
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.narrativeId,
  );

  if (narrKey === -1) return state;

  const newState = cloneDeep(state);
  const narr = newState[SECTION_KEY].data.narratives[narrKey];

  narr.saved = false;
  narr.autosaved = false;
  narr.canceled = false;
  narr.locked = false;

  newState[SECTION_KEY].data.narratives[narrKey] = narr;

  return newState;
};

export const restoreVersionMultiNarrativeSuccess = (
  state: IViewDataInitialState,
  action: NarrativeIdAction & { content: string },
): IViewDataInitialState => {
  const narrKey = state[SECTION_KEY].data.narratives.findIndex(
    (item: NarrativeRef) => item.id === action.narrativeId,
  );

  if (narrKey === -1) return state;

  const newState = cloneDeep(state);
  newState[SECTION_KEY].data.narratives[narrKey] = {
    ...newState[SECTION_KEY].data.narratives[narrKey],
    content: action.content,
  };
  return newState;
};

export const setNarrative = (
  state: IViewDataInitialState,
  action: { value: unknown },
): IViewDataInitialState => ({
  ...state,
  [SECTION_KEY]: {
    ...state[SECTION_KEY],
    data: {
      ...state[SECTION_KEY].data,
      content: action.value,
    },
  },
});

export const setExtraNarrative = (
  state: IViewDataInitialState,
  action: { id: number; value: unknown },
): IViewDataInitialState => {
  const newState = cloneDeep(state);

  const extraNarratives = newState[SECTION_KEY].data.extra_narratives;
  const narrKey = findKey(
    extraNarratives,
    (item: NarrativeRef) => item.id === action.id,
  );

  newState[SECTION_KEY].data.extra_narratives[narrKey as string].content =
    action.value;

  return newState;
};

export const updateNarrativeInStorage = (
  state: IViewDataInitialState,
  action: { dataKey: string; value: unknown },
): IViewDataInitialState => {
  const newState = cloneDeep(state);
  newState[action.dataKey].data = {
    ...newState[action.dataKey].data,
    content: action.value,
  };
  return newState;
};
